
import {axios} from '../../..';

export function LinkLinkedInAccount() {
  return initiateLinkedInAuth('link');
}

export async function UnlinkLinkedInAccount(userEmail: string) {
  try {
    const response = await unlinkLinkedInAccountWithCredentials(userEmail);
    if (response.status === 'success') {
      window.dispatchEvent(new CustomEvent('setSuccessMessage', {
        detail: { message: 'LinkedIn account successfully unlinked!' },
      }));
      const successEvent = new CustomEvent('linkedinUnlinkSuccess', {
        detail: { message: 'LinkedIn account successfully unlinked!' }
      });
      
      window.dispatchEvent(successEvent);
      return response;
    } else {
      throw new Error(response.message || 'Failed to unlink account');
    }
  } catch (error: any) {
    const errorEvent = new CustomEvent('linkedinUnlinkError', {
      detail: { message: error.message || 'Failed to unlink LinkedIn account' }
    });
    window.dispatchEvent(errorEvent);
    throw error;
  }
}

export function LoginWithLinkedIn() {
  initiateLinkedInAuth('login');
}

async function initiateLinkedInAuth(action: 'link' | 'login') {
  try {
    const currentUrl = window.location.origin;
    const redirectUri = `${currentUrl}/auth/linkedin/callback`;

    sessionStorage.setItem('linkedinAuthAction', action);

    const url = action === 'link' ? "/linkedin-sso" : "/linkedin-sso?action=initiateLinkedInSSOLogin";
    const response = await axios.Post(url, { redirectUri });

    if (response.data && response.data.authUrl) {
      const popup = window.open(
        response.data.authUrl,
        'linkedinAuth',
        'width=700,height=800,scrollbars=yes,resizable=yes,top=0,left=0'
      );

      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed);
          window.location.reload();
        }
      }, 1000);

      const handleMessage = async (event: MessageEvent) => {
        if (event.origin !== window.location.origin) {
          return;
        }
        if (event.data.type === 'LINKEDIN_AUTH_NOT_LINKED') {
          popup?.close();
          clearInterval(checkClosed);
          window.removeEventListener('message', handleMessage);
          showLinkRequiredMessage();
          return;
        }

        if (event.data.type === 'LINKEDIN_AUTH_ACCOUNT_ALREADY_LINKED') {
          popup?.close();
          clearInterval(checkClosed);
          window.dispatchEvent(new CustomEvent('setErrorMessage', {
            detail: { message: event.data.data.message },
          }));
          window.removeEventListener('message', handleMessage);
          return;
        }

        if (event.data.type === 'LINKEDIN_AUTH_SUCCESS') {
          clearInterval(checkClosed);
          window.removeEventListener('message', handleMessage);
          if (action === 'link') {
            handleAuthSuccess(event.data, action).then(() => {
              popup?.close();
            }).catch(() => {
              popup?.close();
            });
            return true;
          } else {
            popup?.close();
            await handleAuthSuccess(event.data, action);
          }
        } else if (event.data.type === 'LINKEDIN_AUTH_ERROR') {
          popup?.close();
          clearInterval(checkClosed);
          window.removeEventListener('message', handleMessage);
          window.dispatchEvent(new CustomEvent('linkedinLoginError', {
            detail: {
              message:
                typeof event.data.error === 'string'
                  ? event.data.error
                  : event.data.error?.message ||
                    'LinkedIn login failed. Please try again.',
              code: event.data.code,
            },
          }));
        }
      };

      window.addEventListener('message', handleMessage);
    }
  } catch (error: any) {
    console.error("Error initiating LinkedIn authentication:", error);
  }
}

async function handleAuthSuccess(data: any, action: 'link' | 'login') {
  if (action === 'login') {
    if (data.action === 'login') {
      const tokens = data.data.tokens || {};
      const loginEvent = new CustomEvent('linkedinLoginSuccess', {
        detail: { 
          tokens: tokens,
          user: data.user,
          message: 'B2C LinkedIn SSO login successful'
        }
      });
      window.dispatchEvent(loginEvent);
    } else if (data.action === 'link_required') {
      showLinkRequiredMessage();
    }
  } else if (action === 'link') {
    if (data.action === 'link_required') {
      await handleAccountLinking(data.data.linkedinAccountId, data.data.linkedinEmail);
    }
  }
}

async function handleAccountLinking(linkedinAccountId: string, linkedinEmail: string) {
  try {
    const currentUserId = getCurrentUserId();
    if (!currentUserId) {
      throw new Error('Unable to get current user id');
    }
    const response = await linkLinkedInAccountWithCredentials(
      currentUserId,
      linkedinAccountId,
      linkedinEmail
    );
    if (response.status === 'success') {
      window.dispatchEvent(new CustomEvent('setSuccessMessage', {
        detail: { message: 'LinkedIn account successfully linked!' },
      }));
      window.dispatchEvent(new CustomEvent('linkedinLinkSuccess', {
        detail: { 
          message: 'LinkedIn account successfully linked!',
          linkedinAccountId
        }
      }));
    } else {
      throw new Error(response.message || 'Failed to link account');
    }
  } catch (error: any) {
    const errorEvent = new CustomEvent('linkedinLinkError', {
      detail: { message: error.message || 'Failed to link LinkedIn account' }
    });
    window.dispatchEvent(errorEvent);
    throw error;
  }
}

function getCurrentUserId(): string | null {
  let tokenDecoded = sessionStorage.getItem('token_decoded');
  if (!tokenDecoded) {
    const getCookie = (name: string): string | null => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) {
        return parts.pop()?.split(';').shift() || null;
      }
      return null;
    };
    tokenDecoded = getCookie('token_decoded');
  }
  if (tokenDecoded) {
    try {
      const decoded = JSON.parse(tokenDecoded);
      return decoded.oid || decoded.sub || null;
    } catch {}
  }
  return null;
}

function showLinkRequiredMessage() {
  const event = new CustomEvent('socialAccountLinkingRequired', {
    detail: { message: 'Please log in with your regular credentials to link this LinkedIn account.', socialProvider: 'linkedin' }
  });
  window.dispatchEvent(event);
}

export async function linkLinkedInAccountWithCredentials(userId: string, linkedinAccountId: string, linkedinEmail: string) {
  try {
    const response = await axios.Post("/linkedin-sso?action=link", {
      userId,
      linkedinAccountId,
      linkedinEmail
    });
    return response.data;
  } catch (error) {
    console.error('Error linking LinkedIn account:', error);
    throw error;
  }
}

export async function unlinkLinkedInAccountWithCredentials(userId: string) {
  try {
    const response = await axios.Post("/linkedin-sso?action=unlink", { userId });
    return response.data;
  } catch (error) {
    console.error('Error unlinking LinkedIn account:', error);
    throw error;
  }
}

export async function getLinkedInLinkStatus(username: string) {
  try {
    const response = await axios.Get(`/linkedin-sso?action=status&username=${username}`);
    return response.data;
  } catch (error) {
    console.error('Error getting LinkedIn link status:', error);
    throw error;
  }
}


